import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, CheckCircle2, Circle, AlertTriangle, Shield, 
  Building2, FileText, CreditCard, Lock, Calendar, 
  BadgeCheck, Store, Bot, Users, TrendingUp, ExternalLink,
  ChevronDown, ChevronUp
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const LaunchCompliancePage = () => {
  const [expandedSections, setExpandedSections] = useState({
    launch: true,
    roadmap: false,
    trademark: false,
    appstore: false,
    guardrails: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Checklist state
  const [checklist, setChecklist] = useState({
    // Business & Tax
    businessRegistered: false,
    panLinked: false,
    gstRegistered: false,
    // Legal Policies
    tosLive: true,
    privacyLive: true,
    refundLive: true,
    upscDisclaimer: true,
    noGuarantee: true,
    // Policy Placement
    footerVisible: true,
    pricingConsent: true,
    checkoutCheckbox: true,
    signupConsent: true,
    settingsLegal: false,
    paymentConfirm: false,
    // Payments
    gatewayKyc: false,
    refundLinked: false,
    supportEmail: true,
    // Data Protection
    userConsent: true,
    deletionOption: false,
    secureStorage: true
  });

  const toggleCheck = (key) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const CheckItem = ({ id, label, note, status }) => {
    const isChecked = checklist[id];
    return (
      <div 
        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
          isChecked ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
        }`}
        onClick={() => toggleCheck(id)}
      >
        {isChecked ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
        ) : (
          <Circle className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-medium ${isChecked ? 'text-emerald-800' : 'text-slate-700'}`}>
            {label}
          </p>
          {note && (
            <p className="text-xs text-slate-500 mt-1">{note}</p>
          )}
        </div>
        {status && (
          <Badge className={`text-xs ${
            status === 'ready' ? 'bg-emerald-100 text-emerald-700' : 
            status === 'pending' ? 'bg-amber-100 text-amber-700' : 
            'bg-red-100 text-red-700'
          }`}>
            {status === 'ready' ? '✔ Ready' : status === 'pending' ? '⏳ Pending' : '❌ Missing'}
          </Badge>
        )}
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title, section, color = "indigo" }) => {
    const colorClasses = {
      indigo: "bg-indigo-50 hover:bg-indigo-100 [&_.icon-bg]:bg-indigo-100 [&_.icon]:text-indigo-600",
      emerald: "bg-emerald-50 hover:bg-emerald-100 [&_.icon-bg]:bg-emerald-100 [&_.icon]:text-emerald-600",
      amber: "bg-amber-50 hover:bg-amber-100 [&_.icon-bg]:bg-amber-100 [&_.icon]:text-amber-600",
      blue: "bg-blue-50 hover:bg-blue-100 [&_.icon-bg]:bg-blue-100 [&_.icon]:text-blue-600",
      red: "bg-red-50 hover:bg-red-100 [&_.icon-bg]:bg-red-100 [&_.icon]:text-red-600"
    };
    
    const iconBgClasses = {
      indigo: "bg-indigo-100",
      emerald: "bg-emerald-100",
      amber: "bg-amber-100",
      blue: "bg-blue-100",
      red: "bg-red-100"
    };
    
    const iconClasses = {
      indigo: "text-indigo-600",
      emerald: "text-emerald-600",
      amber: "text-amber-600",
      blue: "text-blue-600",
      red: "text-red-600"
    };
    
    const bgClasses = {
      indigo: "bg-indigo-50 hover:bg-indigo-100",
      emerald: "bg-emerald-50 hover:bg-emerald-100",
      amber: "bg-amber-50 hover:bg-amber-100",
      blue: "bg-blue-50 hover:bg-blue-100",
      red: "bg-red-50 hover:bg-red-100"
    };
    
    return (
      <div 
        className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${bgClasses[color]}`}
        onClick={() => toggleSection(section)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBgClasses[color]}`}>
            <Icon className={`w-5 h-5 ${iconClasses[color]}`} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        </div>
        {expandedSections[section] ? (
          <ChevronUp className="w-5 h-5 text-slate-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-500" />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-100 mb-6">
            <Shield className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            Launch & Compliance Governance
          </h1>
          <p className="text-slate-500 max-w-xl mx-auto">
            CLARIAS compliance checklist for legal launch-readiness, app-store compliance, and consumer-law safety.
          </p>
        </div>

        <div className="space-y-6">
          
          {/* ══════════════════════════════════════════════════════════════════
              SECTION 1: LAUNCH-DAY LEGAL CHECKLIST
          ══════════════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <SectionHeader icon={Calendar} title="1. Launch-Day Legal Checklist (Day 0)" section="launch" />
            
            {expandedSections.launch && (
              <CardContent className="p-6 space-y-6">
                {/* A. Business & Tax */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-slate-600" />
                    <h3 className="font-semibold text-slate-800">A. Business & Tax</h3>
                  </div>
                  <div className="space-y-2">
                    <CheckItem 
                      id="businessRegistered" 
                      label="Business registered (Proprietorship / LLP / Pvt Ltd)" 
                      note="Required for payment gateway KYC"
                    />
                    <CheckItem 
                      id="panLinked" 
                      label="PAN linked to business" 
                      note="Required for GST and payments"
                    />
                    <CheckItem 
                      id="gstRegistered" 
                      label="GST registered (18% for SaaS)" 
                      note="Required if turnover exceeds ₹20L/year"
                    />
                  </div>
                </div>

                {/* B. Legal Policies */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-slate-600" />
                    <h3 className="font-semibold text-slate-800">B. Legal Policies</h3>
                  </div>
                  <div className="space-y-2">
                    <CheckItem 
                      id="tosLive" 
                      label="Terms of Service live" 
                      note={<Link to="/terms-of-service" className="text-indigo-600 hover:underline">View Terms →</Link>}
                      status="ready"
                    />
                    <CheckItem 
                      id="privacyLive" 
                      label="Privacy Policy live (DPDP Act 2023 aligned)" 
                      note={<Link to="/privacy-policy" className="text-indigo-600 hover:underline">View Privacy Policy →</Link>}
                      status="ready"
                    />
                    <CheckItem 
                      id="refundLive" 
                      label="Refund & Cancellation Policy live" 
                      note={<Link to="/refund-policy" className="text-indigo-600 hover:underline">View Refund Policy →</Link>}
                      status="ready"
                    />
                    <CheckItem 
                      id="upscDisclaimer" 
                      label="UPSC non-affiliation disclaimer visible" 
                      note="Explicitly states CLARIAS is not affiliated with UPSC or Government of India"
                      status="ready"
                    />
                    <CheckItem 
                      id="noGuarantee" 
                      label='"No guarantee of results" disclaimer added' 
                      note="AI-generated content disclaimer present in Terms of Service"
                      status="ready"
                    />
                  </div>
                </div>

                {/* C. Policy Placement */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Store className="w-4 h-4 text-slate-600" />
                    <h3 className="font-semibold text-slate-800">C. Policy Placement</h3>
                  </div>
                  <div className="space-y-2">
                    <CheckItem 
                      id="footerVisible" 
                      label="Website footer - all policy links visible" 
                      status="ready"
                    />
                    <CheckItem 
                      id="pricingConsent" 
                      label="Pricing page - consent text visible" 
                      status="ready"
                    />
                    <CheckItem 
                      id="checkoutCheckbox" 
                      label="Checkout page - mandatory checkbox consent" 
                      note="User cannot proceed without agreeing"
                      status="ready"
                    />
                    <CheckItem 
                      id="signupConsent" 
                      label="Signup screen - consent text below button" 
                      status="ready"
                    />
                    <CheckItem 
                      id="settingsLegal" 
                      label="App Settings → Legal section" 
                      note="For mobile app compliance"
                      status="pending"
                    />
                    <CheckItem 
                      id="paymentConfirm" 
                      label="Payment confirmation screen - refund policy link" 
                      status="pending"
                    />
                  </div>
                </div>

                {/* D. Payments */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-4 h-4 text-slate-600" />
                    <h3 className="font-semibold text-slate-800">D. Payments</h3>
                  </div>
                  <div className="space-y-2">
                    <CheckItem 
                      id="gatewayKyc" 
                      label="Payment gateway KYC completed" 
                      note="Razorpay account verified"
                    />
                    <CheckItem 
                      id="refundLinked" 
                      label="Refund policy URL linked in gateway dashboard" 
                    />
                    <CheckItem 
                      id="supportEmail" 
                      label="Support email configured (support@clarias.in)" 
                      status="ready"
                    />
                  </div>
                </div>

                {/* E. Data Protection */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="w-4 h-4 text-slate-600" />
                    <h3 className="font-semibold text-slate-800">E. Data Protection</h3>
                  </div>
                  <div className="space-y-2">
                    <CheckItem 
                      id="userConsent" 
                      label="User consent captured at signup" 
                      status="ready"
                    />
                    <CheckItem 
                      id="deletionOption" 
                      label="Account deletion / data deletion option available" 
                      note="Required under DPDP Act 2023"
                    />
                    <CheckItem 
                      id="secureStorage" 
                      label="Secure data storage in place" 
                      note="Encrypted storage, secure cloud infrastructure"
                      status="ready"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-slate-100 rounded-xl p-4">
                  <p className="text-sm text-slate-600">
                    <strong>Summary:</strong> Complete all checklist items before launching to ensure legal readiness. 
                    Items marked ✔ Ready are already implemented in CLARIAS.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* ══════════════════════════════════════════════════════════════════
              SECTION 2: POST-LAUNCH COMPLIANCE ROADMAP
          ══════════════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <SectionHeader icon={TrendingUp} title="2. Post-Launch Compliance Roadmap (3-6 Months)" section="roadmap" color="emerald" />
            
            {expandedSections.roadmap && (
              <CardContent className="p-6 space-y-6">
                {/* Month 1 */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <h3 className="font-semibold text-slate-800 mb-3">Month 1 — Monitor & Respond</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      Monitor refund disputes and handle within 48 hours
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      Monitor consumer complaints and respond professionally
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      Ensure support email responsiveness (target: 24-hour reply)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      Track app store policy updates (Google Play / Apple)
                    </li>
                  </ul>
                </div>

                {/* Month 2-3 */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-slate-800 mb-3">Month 2–3 — Refine & Improve</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      Review and update legal policies based on user feedback
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      Add Grievance Officer mention (email contact allowed)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      Improve data deletion workflow if requests increase
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      Document refund patterns for policy optimization
                    </li>
                  </ul>
                </div>

                {/* Month 4-6 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold text-slate-800 mb-3">Month 4–6 — Scale & Prepare</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Legal review of policies (optional, recommended for scale)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Prepare infrastructure for more users and payments
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Review AI disclaimer relevance with evolving features
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Prepare international compliance if expanding beyond India
                    </li>
                  </ul>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-emerald-800">
                    <strong>Note:</strong> These are maintenance steps, not approvals. 
                    No government permission is required for ongoing operations.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* ══════════════════════════════════════════════════════════════════
              SECTION 3: TRADEMARK FILING STEPS
          ══════════════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <SectionHeader icon={BadgeCheck} title="3. Trademark Filing Steps (CLARIAS)" section="trademark" color="amber" />
            
            {expandedSections.trademark && (
              <CardContent className="p-6 space-y-6">
                {/* When to File */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">A</span>
                    When to File
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-600 ml-8">
                    <li>• Can be filed before or after launch</li>
                    <li>• Filing early gives brand protection from day one</li>
                    <li>• Protection starts from filing date, not registration date</li>
                  </ul>
                </div>

                {/* Where to File */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">B</span>
                    Where to File
                  </h3>
                  <div className="ml-8">
                    <a 
                      href="https://ipindia.gov.in" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
                    >
                      ipindia.gov.in
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <p className="text-sm text-slate-500 mt-1">Intellectual Property India - Official Trademark Portal</p>
                  </div>
                </div>

                {/* What to File */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">C</span>
                    What to File
                  </h3>
                  <div className="ml-8 bg-amber-50 rounded-xl p-4">
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li><strong>Wordmark:</strong> "CLARIAS"</li>
                      <li><strong>Class:</strong> 41 (Education / Training / Digital Learning)</li>
                      <li><strong>Type:</strong> Wordmark (text only) or Logo (if applicable)</li>
                    </ul>
                  </div>
                </div>

                {/* Documents Needed */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">D</span>
                    Documents Needed
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-600 ml-8">
                    <li>• Applicant name (Individual / Company)</li>
                    <li>• Business address</li>
                    <li>• Logo file (if filing logo trademark)</li>
                    <li>• Description of services (AI-powered UPSC preparation platform)</li>
                    <li>• Identity proof / Certificate of Incorporation</li>
                  </ul>
                </div>

                {/* Cost & Timeline */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">E</span>
                    Cost & Timeline
                  </h3>
                  <div className="ml-8 grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="font-medium text-slate-800">Filing Fee</p>
                      <ul className="text-sm text-slate-600 mt-2 space-y-1">
                        <li>• Individual/Startup: ~₹4,500</li>
                        <li>• Company: ~₹9,000</li>
                        <li>• (per class, online filing)</li>
                      </ul>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="font-medium text-slate-800">Timeline</p>
                      <ul className="text-sm text-slate-600 mt-2 space-y-1">
                        <li>• Filing: Same day</li>
                        <li>• Examination: 1-3 months</li>
                        <li>• Registration: 12-18 months</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> Trademark is NOT mandatory to launch. 
                    However, it is strongly recommended for brand protection at scale.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* ══════════════════════════════════════════════════════════════════
              SECTION 4: APP STORE REJECTION-PROOF CHECKLIST
          ══════════════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <SectionHeader icon={Store} title="4. App Store Rejection-Proof Checklist" section="appstore" color="blue" />
            
            {expandedSections.appstore && (
              <CardContent className="p-6 space-y-6">
                {/* Google Play Store */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <span className="text-lg">▶️</span>
                    </div>
                    <h3 className="font-semibold text-slate-800">Google Play Store</h3>
                  </div>
                  <div className="space-y-2 ml-2">
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Privacy Policy URL added</p>
                        <p className="text-xs text-emerald-600">Must be provided in Play Console</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Subscription details clear</p>
                        <p className="text-xs text-emerald-600">Pricing, duration, and renewal terms visible</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Refund policy disclosed</p>
                        <p className="text-xs text-emerald-600">Digital services refund terms clearly stated</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">No misleading claims</p>
                        <p className="text-xs text-emerald-600">No "guaranteed selection" or "100% success" language</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">No exam guarantee language</p>
                        <p className="text-xs text-emerald-600">AI assistance disclaimer present</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">AI-based nature clearly stated</p>
                        <p className="text-xs text-emerald-600">App description mentions AI-powered features</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Apple App Store */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <span className="text-lg">🍎</span>
                    </div>
                    <h3 className="font-semibold text-slate-800">Apple App Store</h3>
                  </div>
                  <div className="space-y-2 ml-2">
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Privacy Policy URL provided</p>
                        <p className="text-xs text-emerald-600">Required in App Store Connect</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Clear subscription explanation</p>
                        <p className="text-xs text-emerald-600">Auto-renewal terms clearly disclosed</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">No false claims</p>
                        <p className="text-xs text-emerald-600">Honest representation of app capabilities</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">No human tutor misrepresentation</p>
                        <p className="text-xs text-emerald-600">Clearly states AI-generated, not human tutoring</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">AI disclosure included</p>
                        <p className="text-xs text-emerald-600">Users informed of AI-based responses</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Status:</strong> ✔ Compliant — All app store requirements are addressed in CLARIAS policies and UI.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* ══════════════════════════════════════════════════════════════════
              SECTION 5: ONGOING LEGAL SAFETY RAILS
          ══════════════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <SectionHeader icon={Shield} title="5. Ongoing Legal Safety Rails" section="guardrails" color="red" />
            
            {expandedSections.guardrails && (
              <CardContent className="p-6 space-y-6">
                {/* A. AI Safety */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-slate-800">A. AI Safety</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600 ml-7">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Always disclose AI-generated responses
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Avoid authoritative language ("definitely", "guaranteed")
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Encourage user judgement for final decisions
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">●</span>
                      Recommend cross-referencing with official sources
                    </li>
                  </ul>
                </div>

                {/* B. Consumer Protection */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-800">B. Consumer Protection</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600 ml-7">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      Transparent pricing — no hidden fees
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      Visible refund rules before payment
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      No dark patterns — clear cancellation flow
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">●</span>
                      Easy-to-find support contact
                    </li>
                  </ul>
                </div>

                {/* C. Marketing Safety */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-slate-800">C. Marketing Safety</h3>
                  </div>
                  <div className="ml-7 bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">⚠️ NEVER Use:</p>
                    <ul className="space-y-1 text-sm text-red-700">
                      <li>❌ "Guaranteed selection" or "100% success"</li>
                      <li>❌ "Official UPSC" or government affiliation claims</li>
                      <li>❌ Topper endorsements without verified proof</li>
                      <li>❌ Misleading before/after comparisons</li>
                      <li>❌ False urgency or scarcity tactics</li>
                    </ul>
                  </div>
                </div>

                {/* D. Data Safety */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-slate-800">D. Data Safety</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600 ml-7">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      Minimal data collection — only what's necessary
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      Honour deletion requests within timeline
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      Secure storage practices — encryption at rest and transit
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">●</span>
                      No selling or trading of user data
                    </li>
                  </ul>
                </div>

                {/* Final Statement */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6 text-center">
                  <p className="text-lg font-medium text-indigo-900">
                    "CLARIAS remains compliant by transparency, not permissions."
                  </p>
                  <p className="text-sm text-indigo-600 mt-2">
                    Trust is built through honest practices, not legal loopholes.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

        </div>

        {/* Footer Links */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500 mb-4">Quick Links to Legal Policies</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/terms-of-service" className="text-indigo-600 hover:underline text-sm">Terms of Service</Link>
            <span className="text-slate-300">|</span>
            <Link to="/privacy-policy" className="text-indigo-600 hover:underline text-sm">Privacy Policy</Link>
            <span className="text-slate-300">|</span>
            <Link to="/refund-policy" className="text-indigo-600 hover:underline text-sm">Refund Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaunchCompliancePage;
